import { CustomCell, CustomRenderer, GridCellKind, ProvideEditorCallback } from '@glideapps/glide-data-grid'
import { atom } from 'jotai'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'

import { IImageCell, ImageCell } from '@/core/models/columns/image'
import { STORAGE_KEYS } from '@/shared/constants'

import { ImageManager } from '../../ImageManager/ImageManager'
import { drawImage } from '../helpers/draw-image'
import { getEditorStyle } from '../helpers/editor-style-helper'

// Font style for icons
const ICON_FONT_STYLE = '16px Material Symbols Rounded'

// Interface definitions
interface CustomImageCellProps {
  readonly type: ColumnTypes
  readonly cell: IImageCell
  readonly readOnly?: boolean
}

export type CustomImageCell = CustomCell<CustomImageCellProps>

/**
 * Draws a placeholder icon when there's no image in the cell.
 * This function is responsible for rendering the placeholder and handling hover effects.
 *
 * @param {Object} args - Drawing arguments including context, rect, theme, and hover info.
 */
const drawPlaceholder = (args: any) => {
  const { ctx, rect, theme, hoverAmount, hoverX, overrideCursor, cell } = args

  // Draw placeholder icon
  ctx.fillStyle = theme.textDark
  ctx.font = '20px Material Symbols Rounded'
  ctx.fillText('add_box', rect.x + theme.cellHorizontalPadding, rect.y + rect.height / 2)

  // Handle hover effects
  if (hoverAmount > 0 && hoverX !== undefined && overrideCursor) {
    const { x, width: w } = rect
    const hoverPosition = hoverX + x

    ctx.font = ICON_FONT_STYLE
    ctx.fillStyle = theme.textDark

    const addIcon = 'add_box'
    const editIconMetrix = ctx.measureText(addIcon)

    // Calculate add icon position based on alignment
    let startOfAddIconX = x + theme.cellHorizontalPadding
    if (cell.contentAlign === 'right') {
      startOfAddIconX = Math.floor(x + w - theme.cellHorizontalPadding - editIconMetrix.width)
    } else if (cell.contentAlign === 'center') {
      startOfAddIconX = Math.floor(x + w / 2 - editIconMetrix.width / 2)
    }

    const endOfAddIconX = startOfAddIconX + editIconMetrix.width

    // Change cursor to pointer if hovering over add icon
    if (hoverPosition >= startOfAddIconX && hoverPosition <= endOfAddIconX) {
      overrideCursor('pointer')
    }
  }
}

/**
 * Draws edit and delete icons when hovering over a cell with an image.
 * This function handles the rendering of interactive icons for image cells.
 *
 * @param {Object} args - Drawing arguments.
 * @param {Object} cell - The cell object.
 */
const drawHoverIcons = (args: any, cell: any) => {
  const { ctx, rect, theme, hoverX, overrideCursor } = args
  const { x, y, width: w, height: h } = rect
  const imgSize = Math.min(h, w) - theme.cellVerticalPadding * 2

  const spacing = 16
  const centerY = y + h / 2

  // Calculate image position based on alignment
  let startOfImageX = x + theme.cellHorizontalPadding
  if (cell.contentAlign === 'right') {
    startOfImageX = Math.floor(x + w - theme.cellHorizontalPadding - imgSize)
  } else if (cell.contentAlign === 'center') {
    startOfImageX = Math.floor(x + w / 2 - imgSize / 2)
  }

  const endOfImageX = startOfImageX + imgSize

  // Set up context for drawing icons
  ctx.font = ICON_FONT_STYLE
  ctx.fillStyle = theme.textDark

  const editIcon = 'edit'
  const deleteIcon = 'delete'

  const editIconMetrix = ctx.measureText(editIcon)

  // Calculate positions for edit and delete icons
  const editIconStartX = endOfImageX + spacing
  const editIconEndX = editIconStartX + editIconMetrix.width

  const deleteIconStartX = editIconEndX + spacing
  const deleteIconEndX = deleteIconStartX + editIconMetrix.width

  // Draw the icons
  ctx.fillText(editIcon, endOfImageX + spacing, centerY)
  ctx.fillText(deleteIcon, deleteIconStartX, centerY)

  // Change cursor to pointer if hovering over image or icons
  if (
    (hoverX >= startOfImageX - x && hoverX <= endOfImageX - x) ||
    (hoverX >= editIconStartX - x && hoverX <= editIconEndX - x) ||
    (hoverX >= deleteIconStartX - x && hoverX <= deleteIconEndX - x)
  ) {
    overrideCursor('pointer')
  }
}

/**
 * Determines the action to take based on where the user clicked in the cell.
 * This function interprets click events and returns the appropriate action.
 *
 * @param {Object} args - Click event arguments.
 * @returns {string|undefined} The action to be performed.
 */
const handleCellClick = (args: any): string | undefined => {
  const {
    cell,
    posX: clickX,
    bounds: { x, width: w, height: h },
    theme,
  } = args

  const clickPosition = clickX + x
  const spacing = 16

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d', { alpha: false })
  if (!ctx) return ''

  if (cell.data.cell.toString() !== '') {
    // Logic for cells with an image
    const imgSize = Math.min(h, w) - theme.cellVerticalPadding * 2

    // Calculate image position based on alignment
    let startOfImageX = x + theme.cellHorizontalPadding
    if (cell.contentAlign === 'right') {
      startOfImageX = Math.floor(x + w - theme.cellHorizontalPadding - imgSize)
    } else if (cell.contentAlign === 'center') {
      startOfImageX = Math.floor(x + w / 2 - imgSize / 2)
    }

    const endOfImageX = startOfImageX + imgSize

    ctx.font = ICON_FONT_STYLE
    ctx.fillStyle = theme.textDark

    const editIcon = 'edit'
    const deleteIcon = 'delete'

    const editIconMetrix = ctx.measureText(editIcon)
    const deleteIconMetrix = ctx.measureText(deleteIcon)

    // Calculate positions for edit and delete icons
    const editIconStartX = endOfImageX + spacing
    const editIconEndX = editIconStartX + editIconMetrix.width

    const deleteIconStartX = editIconEndX + spacing
    const deleteIconEndX = deleteIconStartX + deleteIconMetrix.width

    // Determine action based on click position
    if (clickPosition >= startOfImageX && clickPosition <= endOfImageX) {
      return 'open_image_modal'
    }
    if (clickPosition >= editIconStartX && clickPosition <= editIconEndX) {
      return 'open_editor'
    }
    if (clickPosition >= deleteIconStartX && clickPosition <= deleteIconEndX) {
      return 'delete'
    }
  } else {
    // Logic for empty cells
    ctx.font = ICON_FONT_STYLE
    ctx.fillStyle = theme.textDark

    const addIcon = 'add_box'
    const editIconMetrix = ctx.measureText(addIcon)

    // Calculate position of add icon based on alignment
    let startOfAddIconX = x + theme.cellHorizontalPadding
    if (cell.contentAlign === 'right') {
      startOfAddIconX = Math.floor(x + w - theme.cellHorizontalPadding - editIconMetrix.width)
    } else if (cell.contentAlign === 'center') {
      startOfAddIconX = Math.floor(x + w / 2 - editIconMetrix.width / 2)
    }

    const endOfAddIconX = startOfAddIconX + editIconMetrix.width

    if (clickPosition >= startOfAddIconX && clickPosition <= endOfAddIconX) {
      return 'open_editor'
    }
  }
}

/**
 * Editor component for the custom image cell.
 * This component provides the interface for editing image cells.
 */
const Editor: ReturnType<ProvideEditorCallback<CustomImageCell>> = ({ value, onFinishedEditing }) => {
  const cell = value.data.cell as ImageCell
  const cellValue = cell.toString()

  if (cell.settings.imgSourceType === 'youtube') return null

  const handleImageChange = (_type: any, imageUrl: unknown) => {
    onFinishedEditing({
      ...value,
      data: { type: value.data.type, cell: cell.toCell(imageUrl as string) as ImageCell },
      copyData: imageUrl as string,
    })
  }

  return (
    <div className='bg-white border bdr'>
      <ImageManager
        modal={false}
        id='mk-gg-img-editor'
        show={true}
        onChange={handleImageChange}
        initialValue={{ url: cellValue }}
      />
    </div>
  )
}

// The state of the image modal
export const imageModalState = atom<{ show: boolean; value: string | null }>({ show: false, value: null })

/**
 * Main renderer object for custom image cells.
 * This object defines how image cells are rendered, edited, and interacted with.
 */
const CustomImageRenderer: CustomRenderer<CustomImageCell> = {
  kind: GridCellKind.Custom,
  isMatch: (cell: CustomCell): cell is CustomImageCell =>
    (cell.data as CustomImageCellProps).type === ColumnTypes.Image,
  needsHover: true,
  needsHoverPosition: true,
  measure: (_ctx, cell) => (Array.isArray(cell.data.cell.value) ? cell.data.cell.value.length * 50 : 50 + 70),

  /**
   * Draws the cell content.
   * This method is responsible for rendering the image or placeholder in the cell.
   */
  draw: (args, cell) => {
    const { hoverAmount, hoverX, overrideCursor } = args
    const imageUrl = cell.data.cell.toString()
    const isReadOnly = cell.data.readOnly

    if (!imageUrl || imageUrl === '') {
      drawPlaceholder(args)
      return
    }

    drawImage(args, imageUrl, 4, args.cell.contentAlign)

    if (!isReadOnly && hoverAmount > 0 && hoverX !== undefined && overrideCursor) {
      drawHoverIcons(args, cell)
    }
  },

  /**
   * Provides the editor component and styling.
   * This method returns the configuration for the cell editor.
   */
  provideEditor: () => {
    const imageManagerTab = localStorage.getItem(STORAGE_KEYS.IMG_MGR_TAB)
    const editorHeight = imageManagerTab === 'stockPhotos' ? 270 : imageManagerTab === 'imageUrl' ? 110 : 616
    const style = {
      maxWidth: '600px',
      width: '600px',
      overflow: 'hidden',
      boxShadow: 'unset',
      backgroundColor: 'transparent',
      transform: 'translateX(0px)',
      ...getEditorStyle(editorHeight, true),
    }
    return { disablePadding: false, disableStyling: false, styleOverride: style, editor: Editor }
  },

  /**
   * Handles pasting content into the cell.
   * This method processes pasted content and updates the cell accordingly.
   */
  onPaste: (toPaste, cell) => {
    const uris = toPaste
      .trim()
      .split(',')
      .filter((f) => {
        try {
          new URL(f)
          return true
        } catch {
          return false
        }
      })
    const cellValue = cell.cell.value?.toString() || ''
    return uris.length === cellValue.length && uris.every((u, i) => u === cellValue[i])
      ? undefined
      : { ...cell, cell: { ...cell.cell, value: uris } }
  },

  /**
   * Handles click events on the cell.
   * This method determines the action to take based on where the cell was clicked.
   */
  onClick: (args: any) => {
    const { preventDefault, cell } = args
    const imageCell = cell.data.cell as ImageCell
    const clickAction = handleCellClick(args)

    switch (clickAction) {
      case 'open_editor':
        return undefined
      case 'open_image_modal':
        preventDefault()
        // see `GridPreviewModal.tsx` for more details
        const imageClickEvent = new CustomEvent('preview-modal-open', {
          detail: { title: 'Image Preview', value: imageCell.toString(), width: 500 },
        })
        document.dispatchEvent(imageClickEvent)
        return undefined
      case 'delete':
        preventDefault()
        args.cell.data.extra.dataEditorRef.current.emit('delete')
        break
      default:
        preventDefault()
    }
  },

  /**
   * Handles deletion of cell content.
   * This method defines how to clear the content of an image cell.
   */
  onDelete: (cellData) => ({
    ...cellData,
    data: { ...cellData.data, cell: cellData.data.cell.toCell('') as IImageCell },
  }),
}

export default CustomImageRenderer
